/**
 * Types de frappe à l'attaque (zones X/V) selon le rôle, la position avant/arrière
 * et la tactique P1 (échange zone 2 / zone 4).
 */
import { playingRoles, type GameSystem } from './gameSystem';

export interface AttackPassTypeContext {
  statTarget: 'us' | 'them';
  selectedPlayer: string;
  court: string[];
  courtRoles: string[];
  playerRoles: Record<string, string>;
  isP1SwapActive: boolean;
  teamTactics: { p1AttackZone2: 'R4' | 'Pointu'; gameSystem?: GameSystem };
  /** Ordre d’affichage terrain (avant / arrière) ; si absent, on utilise `court`. */
  visualCourt?: readonly string[] | null;
}

/** Appels rapides / feintes du central en avant (annonces passeur à la passe, aligné attaque central avant). */
export const CENTRAL_FRONT_QUICK_ATTACK_TYPES: readonly string[] = ['X1', 'X2', 'X7', 'Cf', 'Cd'];

export function getRotationLabelFromCourtRoles(courtRoles: string[]): string {
  const setterIndex = courtRoles.findIndex((r) => r === 'Passeur');
  return setterIndex !== -1 ? `P${setterIndex + 1}` : 'P1';
}

/**
 * Swap visuel / zones d'attaque P1 d'une équipe : elle est en rotation P1 ET en
 * réception (c'est l'AUTRE équipe qui sert).
 *
 * La règle vaut pour les deux équipes. Elle n'était évaluée que pour la nôtre :
 * la rotation P1 de l'adversaire n'existait pas, en mode « équipe adverse » comme
 * en deux équipes.
 */
export function isP1SideOutSwapFor(
  side: 'us' | 'them',
  rotationLabel: string,
  servingSide: 'us' | 'them',
): boolean {
  return servingSide !== side && rotationLabel === 'P1';
}

/** Cas particulier historique : la règle P1 pour NOTRE équipe. */
export function shouldApplyP1SideOutAttackSwap(
  usRotationLabel: string,
  servingSide: 'us' | 'them',
): boolean {
  return isP1SideOutSwapFor('us', usRotationLabel, servingSide);
}

export function getAttackPassTypesForContext(ctx: AttackPassTypeContext): string[] {
  // `statTarget` ne sert plus à distinguer les équipes : l'adversaire suit les
  // mêmes règles que nous (R4 en 4, pointu en 2, frappe zone 1 du pointu en P1,
  // échange tactique P1). Il était figé sur R4 → X5, pointu → X6, sans jamais
  // tenir compte de sa rotation ni de sa tactique.
  const {
    selectedPlayer,
    court,
    courtRoles,
    playerRoles,
    isP1SwapActive,
    teamTactics,
    visualCourt,
  } = ctx;
  // A role the team's system changes wins (VOL-162): in a 5-1 with three outside
  // hitters, the R4 across from the setter attacks as the opposite (X6, X8).
  const courtIdx = court.indexOf(selectedPlayer);
  const played = courtIdx !== -1 && courtIdx < 6 && teamTactics?.gameSystem
    ? playingRoles({ court, courtRoles, playerRoles }, teamTactics.gameSystem)[courtIdx]
    : undefined;
  const systemRole = played !== undefined && played !== courtRoles[courtIdx] ? played : undefined;
  const role = systemRole ?? playerRoles[selectedPlayer] ?? courtRoles[courtIdx];
  const grid = visualCourt && visualCourt.length >= 6 ? visualCourt : court;
  const playerIndex = court.indexOf(selectedPlayer);
  const visualIndex = grid.indexOf(selectedPlayer);
  const rowIndex = visualIndex !== -1 ? visualIndex : playerIndex;
  const isBackRow = rowIndex !== -1 && [0, 4, 5].includes(rowIndex);

  if (role === 'Lib') return [];

  if (isBackRow && (role === 'R4' || role === 'Pointu')) {
    // Pointu physiquement en position 1 (arrière-droite, rowIndex 0) :
    // frappe zone 1 → X8/V8. Les autres positions arrière (P5/P6) restent en pipe.
    if (role === 'Pointu' && rowIndex === 0) {
      return ['X8', 'V8'];
    }
    return ['Xp', 'Vp'];
  }

  if (role === 'R4') {
    const inZone2 = isP1SwapActive && teamTactics.p1AttackZone2 === 'R4';
    return inZone2 ? ['X6', 'V6'] : ['X5', 'V5'];
  }

  if (role === 'Pointu') {
    // Le Pointu suit le swap tactique P1 : avec p1AttackZone2='R4', le Pointu reste
    // en zone 4 et frappe X5/V5 ; sinon il vient en zone 2 et frappe X6/V6.
    const inZone4 = isP1SwapActive && teamTactics.p1AttackZone2 === 'R4';
    return inZone4 ? ['X5', 'V5'] : ['X6', 'V6'];
  }

  if (role === 'Central') {
    if (isBackRow) return ['Xp', 'Vp'];
    return ['X1', 'X2', 'X7', 'Cf', 'Cd'];
  }

  if (role === 'Passeur') {
    if (isBackRow) return ['Xp', 'Vp'];
    // A front-row setter attacks (second hand) from the setting spot, i.e. zone 2,
    // whatever the rotation slot. X8 (zone 1) used to be offered from positions 2 and 3.
    return ['X6', 'V6'];
  }

  return [];
}

export function getDefaultAttackPassType(ctx: AttackPassTypeContext): string | null {
  const types = getAttackPassTypesForContext(ctx);
  return types[0] ?? null;
}
